import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
const ThemeContext = createContext(null);
function getSystemTheme() {
    if (typeof window === "undefined")
        return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
}
export function ThemeProvider({ defaultMode = "system", storageKey = "fmc-theme-mode", children, }) {
    const [mode, setMode] = useState(() => {
        if (typeof window === "undefined")
            return defaultMode;
        const stored = localStorage.getItem(storageKey);
        if (stored === "light" || stored === "dark" || stored === "system") {
            return stored;
        }
        return defaultMode;
    });
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);
    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (e) => {
            setSystemTheme(e.matches ? "dark" : "light");
        };
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);
    // Compute resolved theme
    const resolvedTheme = useMemo(() => {
        if (mode === "system")
            return systemTheme;
        return mode;
    }, [mode, systemTheme]);
    // Apply theme class to document
    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(resolvedTheme);
    }, [resolvedTheme]);
    const updateMode = useCallback((newMode) => {
        setMode(newMode);
        localStorage.setItem(storageKey, newMode);
    }, [storageKey]);
    const toggle = useCallback(() => {
        const nextTheme = resolvedTheme === "dark" ? "light" : "dark";
        updateMode(nextTheme);
    }, [resolvedTheme, updateMode]);
    const value = useMemo(() => ({
        mode,
        resolvedTheme,
        setMode: updateMode,
        toggle,
    }), [mode, resolvedTheme, updateMode, toggle]);
    return (_jsx(ThemeContext.Provider, { value: value, children: children }));
}
export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
}
//# sourceMappingURL=ThemeProvider.js.map